import React, { FC, useState } from "react";
import StatusDialog from "../../Unknown/StatusDialog";
import Box from "../../Unknown/Box";
import TextField from "../../Unknown/TextField";
import Typography from "../../Unknown/Typography";
import { Appointment } from "./types";
import useTranslations from "./useTranslations";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

export type EditDurationDialogProps = {
  appointment: Appointment;
  isOpened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const MIN_DURATION_MINUTES = 1;
const MAX_DURATION_MINUTES = 600;

const EditDurationDialog: FC<EditDurationDialogProps> = ({
  appointment,
  isOpened,
  onClose,
  onSuccess,
}) => {
  const translations = useTranslations({
    scheduledAt: appointment.createdAtCenterTimezone,
    originalAppointmentAt: appointment.originalAppointment?.startedAt,
    additionalAppointmentAt: appointment.additionalViewsAppointment?.startedAt,
    durationMinutes: appointment.durationMinutes,
  });

  const editAppointmentDuration = useFirebaseAppFunction(
    "editAppointmentDuration",
  );

  const [durationMinutes, setDurationMinutes] = useState<number | "">(
    appointment.durationMinutes,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isDurationValid =
    durationMinutes !== "" &&
    Number(durationMinutes) >= MIN_DURATION_MINUTES &&
    Number(durationMinutes) <= MAX_DURATION_MINUTES;

  const handleSave = async () => {
    if (!durationMinutes || !isDurationValid) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      await editAppointmentDuration({
        appointmentId: appointment.id,
        durationMinutes: Number(durationMinutes),
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update duration",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      setDurationMinutes("");
      return;
    }

    const value = parseInt(inputValue, 10);
    if (!Number.isNaN(value) && value >= 0) {
      setDurationMinutes(value);
    }
  };

  const isDirty =
    durationMinutes !== "" && durationMinutes !== appointment.durationMinutes;

  const showDurationError = durationMinutes !== "" && !isDurationValid;

  const actionButtons = [
    {
      key: "cancel",
      text: translations.cancel,
      onClick: onClose,
      disabled: isLoading,
    },
    {
      key: "save",
      text: translations.save,
      onClick: handleSave,
      disabled:
        isLoading || durationMinutes === "" || !isDurationValid || !isDirty,
    },
  ];

  return (
    <StatusDialog
      open={isOpened}
      title={translations.editDurationTitle}
      actionButtons={actionButtons}
      isLoading={isLoading}
      errorMessage={errorMessage}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 540,
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Typography variant="body1">
          {translations.editDurationHelperText}
        </Typography>
        <TextField
          label={translations.editDurationLabel}
          type="number"
          value={durationMinutes}
          onChange={handleDurationChange}
          fullWidth
          inputProps={{
            min: MIN_DURATION_MINUTES,
            max: MAX_DURATION_MINUTES,
            step: 1,
          }}
          error={showDurationError}
          helperText={
            showDurationError
              ? translations.editDurationValidationError
              : undefined
          }
          disabled={isLoading}
        />
      </Box>
    </StatusDialog>
  );
};

export default EditDurationDialog;
